import { useState } from 'react'
import { useToday } from '../state/today'
import { useToast } from '../state/toast'
import { Button, Sheet } from './ui'

/**
 * The "you have moved" prompt.
 *
 * A worker can be on three sites in a day, and the failure mode that costs the
 * company real money is time billed to the wrong job. The watcher only fires
 * after a dwell period, and the switch closes one entry and opens the next in a
 * single server call, so there is no window where the day has a hole in it.
 */
export function SiteChangeSheet() {
  const { siteChange, resolveSiteChange, clockIn, clockOut, switchSite, busy } = useToday()
  const toast = useToast()
  const [working, setWorking] = useState<string | null>(null)

  if (!siteChange) return null

  const { kind, site, openSite } = siteChange
  const distance = Math.round(siteChange.fix.accuracy)

  const run = async (action: string, fn: () => Promise<void>, resolution: Parameters<typeof resolveSiteChange>[0]) => {
    setWorking(action)
    try {
      await fn()
      resolveSiteChange(resolution)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'That did not go through')
    } finally {
      setWorking(null)
    }
  }

  // Left every geofence while still on the clock.
  if (kind === 'departed') {
    return (
      <Sheet
        open
        onClose={() => resolveSiteChange('dismissed')}
        title="Still on the clock"
        footer={
          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              size="lg"
              loading={working === 'out'}
              disabled={busy}
              onClick={() => void run('out', clockOut, 'clocked_out')}
            >
              Clock out of {openSite?.name ?? 'site'}
            </Button>
            <Button variant="ghost" onClick={() => resolveSiteChange('dismissed')}>
              Stay clocked in
            </Button>
          </div>
        }
      >
        <p className="text-base">
          Your phone says you have left <strong>{openSite?.name ?? 'the site'}</strong>, but your
          clock is still running there.
        </p>
        <p className="mt-3 text-sm text-graphite-2">
          If you are on a supply run or moving between buildings, stay clocked in.
        </p>
      </Sheet>
    )
  }

  // Arrived somewhere with nothing running.
  if (!openSite) {
    return (
      <Sheet
        open
        onClose={() => resolveSiteChange('dismissed')}
        title={`You are at ${site?.name ?? 'a site'}`}
        footer={
          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              size="lg"
              loading={working === 'in'}
              disabled={busy || !site}
              onClick={() => site && void run('in', () => clockIn(site.id), 'clocked_in')}
            >
              Clock in here
            </Button>
            <Button variant="ghost" onClick={() => resolveSiteChange('dismissed')}>
              Not now
            </Button>
          </div>
        }
      >
        <p className="text-base">
          You have been at <strong>{site?.name}</strong> for a few minutes and you are not on the
          clock.
        </p>
        <p className="mt-3 font-mono text-xs text-graphite-2">Location accurate to ±{distance} m</p>
      </Sheet>
    )
  }

  // Arrived at a different site while still clocked in elsewhere.
  return (
    <Sheet
      open
      onClose={() => resolveSiteChange('dismissed')}
      title="You have moved sites"
      footer={
        <div className="flex flex-col gap-2">
          <Button
            variant="primary"
            size="lg"
            loading={working === 'switch'}
            disabled={busy || !site}
            onClick={() => site && void run('switch', () => switchSite(site.id), 'switched')}
          >
            Move my clock to {site?.name}
          </Button>
          <Button
            variant="secondary"
            loading={working === 'out'}
            disabled={busy}
            onClick={() => void run('out', clockOut, 'clocked_out')}
          >
            Just clock out of {openSite.name}
          </Button>
          <Button variant="ghost" onClick={() => resolveSiteChange('dismissed')}>
            Leave it as is
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span
            className="size-3 shrink-0 rounded-full"
            style={{ background: openSite.color }}
            aria-hidden
          />
          <div>
            <p className="label">Clock is running at</p>
            <p className="font-bold">{openSite.name}</p>
          </div>
        </div>
        <div className="ml-1.5 h-6 w-px bg-line-strong" aria-hidden />
        <div className="flex items-center gap-3">
          <span
            className="size-3 shrink-0 rounded-full"
            style={{ background: site?.color }}
            aria-hidden
          />
          <div>
            <p className="label">You are standing at</p>
            <p className="font-bold">{site?.name}</p>
          </div>
        </div>
      </div>
      <p className="mt-4 text-sm text-graphite-2">
        Moving your clock closes {openSite.name} at the current time and opens {site?.name} at the
        same moment, so nothing is lost between them.
      </p>
    </Sheet>
  )
}
